import {Card, Container, Group, Menu, rem, Space, Tabs, TabsValue, Text, Title, UnstyledButton} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback} from "react";
import {DVIREntity} from "@ots-frontend/ots-data";
import {DVIRDetails} from "./DVIRDetails";
import {useTranslation} from "react-i18next";
import {DVIRActionMenuDropdown} from "../../menu/DVIRActionMenu/DVIRActionMenu";
import {createContentStyles} from "../../../theme/useContentStyles";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {TabItem} from "../../tabs/Tabs/Tabs";
import {ObjectChangeLogView} from "../common/ObjectChangeLogView/ObjectChangeLogView";

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type DVIRProfileProps = PropsWithChildren<{
  dvir: DVIREntity
}>

const tabs: TabItem[] = [
  {
    key: "details",
    value: "Details"
  },
  {
    key: "history",
    value: "Change Logs",
    permissions: ["change_log_view"]
  },
];

export const DVIRProfile = (props: DVIRProfileProps) => {
  const {dvir} = props;

  const {classes} = useStyles();

  const {t} = useTranslation(["common"])
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation()

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key} icon={tab.icon}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  const onTabChange = useCallback((value: TabsValue) => {
    navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})
  }, [navigate, pathname, search, state])

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">

          <Title order={4}>
            {t("common.entity.dvir", "DVIR")}
          </Title>

          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <DVIRActionMenuDropdown dvir={dvir}/>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={onTabChange}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <DVIRDetails dvir={dvir}/>
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <ObjectChangeLogView entity={dvir}/>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
